import { useMediaQuery } from '@mui/material';
import { FC } from 'react';
import { useTranslation } from 'react-i18next';
import { SecondaryTextTypography } from '../../../../../App.styles';
import { useThemeContext } from '../../../../../global-state/themeContext';
import { Icon, Icons, Tooltip, Typography } from '../../../../../stories/atoms';
import { ColumnFlexContainer, RowFlexContainer } from '../../../../../stories/atoms/containerV2/ContainerV2';
import { IconWithText } from '../../../../../stories/molecules';
import { Colors, muiTheme, percentToHex, Typographies } from '../../../../../theme';
import { homeTranslations } from '../../../../../translations';
import { StyledThresholdText, StyledUppercaseText } from './OverviewContent.styles';

const PICK_PACK_MINUTES_THRESHOLD = 7;

const getMinutesTextAndSymbol = (minutes: number) => {
  const minutesText = minutes.toFixed(1);

  if (minutes >= 60) {
    const hours = minutes / 60;
    return { text: hours.toFixed(1), symbol: 'hrs' };
  }

  return { text: minutesText, symbol: 'mins' };
};

const minutesToHHMM = (minutes: number) =>
  `${String(Math.trunc(minutes / 60)).padStart(2, '0')}:${String(Math.trunc(minutes % 60)).padStart(2, '0')}`;

const getTresholdColor = (isAboveThreshold: boolean, isDarkMode: boolean): Colors => {
  return isDarkMode ? (isAboveThreshold ? 'error' : 'success') : isAboveThreshold ? 'red' : 'validationGreen';
};

type OverviewItemProps = {
  title: string;
  value: string;
  symbol: string;
  tooltipText: string;
};

const OverviewItem = ({ title, value, symbol, tooltipText }: OverviewItemProps) => {
  const { mode } = useThemeContext();
  const isDarkMode = mode === 'dark';
  const isMobile = useMediaQuery(muiTheme.breakpoints.down('tabletPortrait'));

  return (
    <ColumnFlexContainer
      flex={1}
      padding={[4]}
      borderRadius={[2]}
      justifyContent="between"
      position="relative"
      backgroundColor={isDarkMode ? 'backgroundSelectedDark' : 'backgroundTableHeader'}
    >
      <StyledUppercaseText type={isMobile ? 'body4' : 'button'}>{title}</StyledUppercaseText>
      <RowFlexContainer alignItems="baseline" gap={[1]}>
        <Typography type="h2" lineHeight={1}>
          {value}
        </Typography>
        <SecondaryTextTypography>{symbol}</SecondaryTextTypography>
      </RowFlexContainer>
      <RowFlexContainer position="absolute" top="8px" right="8px">
        <Tooltip
          text={
            <Typography type="caption" color="white">
              {tooltipText}
            </Typography>
          }
          position="bottom"
          arrowedToolTip
        >
          <Icon icon={Icons.INFO} size="small" />
        </Tooltip>
      </RowFlexContainer>
    </ColumnFlexContainer>
  );
};

const IconSeparator = ({ icon }: { icon: Icons }) => {
  const { mode } = useThemeContext();
  const isDarkMode = mode === 'dark';

  return (
    <ColumnFlexContainer flex={1}>
      <ColumnFlexContainer
        padding={[1]}
        borderRadius={[100]}
        alignItems="center"
        justifyContent="center"
        alignSelf="center"
        hasBorder
      >
        <IconWithText
          iconOptions={{
            icon: icon,
            color: isDarkMode ? 'white' : 'primary',
            size: 'small',
          }}
        />
      </ColumnFlexContainer>
    </ColumnFlexContainer>
  );
};

const MinutesItem = ({
  label,
  value,
  symbol,
  color,
  valueTextType = 'h3',
}: {
  label: string;
  value: string;
  symbol: string;
  color?: Colors;
  valueTextType?: Typographies;
}) => {
  return (
    <ColumnFlexContainer alignItems="start" gap={[2]}>
      <RowFlexContainer alignItems="baseline" gap={[2]}>
        <StyledThresholdText type={valueTextType} lineHeight={1} shadeColor={color ? color : 'inherit'}>
          {value}
        </StyledThresholdText>
        <SecondaryTextTypography type="legal">{symbol}</SecondaryTextTypography>
      </RowFlexContainer>
      <SecondaryTextTypography type="legal">{label}</SecondaryTextTypography>
    </ColumnFlexContainer>
  );
};

type OverviewContentProps = {
  turnaroundTimeMinutes: number;
  customerPromise: number;
  pickMinutes: number;
  packMinutes: number;
};

export const OverviewContent: FC<OverviewContentProps> = ({
  turnaroundTimeMinutes,
  customerPromise,
  pickMinutes,
  packMinutes,
}) => {
  const { mode } = useThemeContext();
  const isDarkMode = mode === 'dark';
  const isMobile = useMediaQuery(muiTheme.breakpoints.down('tabletPortrait'));

  const { t } = useTranslation();
  const { bopisLeadTime: bopisLeadTimeTranslations } = homeTranslations;

  const isAboveThreshold = pickMinutes + packMinutes > PICK_PACK_MINUTES_THRESHOLD;

  return (
    <ColumnFlexContainer flex={1} padding={[4]} gap={[2]}>
      <RowFlexContainer flex={1} gap={[2]}>
        <OverviewItem
          title={t(
            bopisLeadTimeTranslations.turnaroundTime.key,
            bopisLeadTimeTranslations.turnaroundTime.defaultValue,
          )}
          value={minutesToHHMM(turnaroundTimeMinutes)}
          symbol="HH:MM"
          tooltipText={t(
            bopisLeadTimeTranslations.turnaroundTimeTooltipText.key,
            bopisLeadTimeTranslations.turnaroundTimeTooltipText.defaultValue,
          )}
        />

        <OverviewItem
          title={t(
            bopisLeadTimeTranslations.customerPromise.key,
            bopisLeadTimeTranslations.customerPromise.defaultValue,
          )}
          value={customerPromise.toFixed(2)}
          symbol="%"
          tooltipText={t(
            bopisLeadTimeTranslations.customerPromiseTooltipText.key,
            bopisLeadTimeTranslations.customerPromiseTooltipText.defaultValue,
          )}
        />
      </RowFlexContainer>

      <ColumnFlexContainer
        flex={1}
        backgroundColor={isDarkMode ? 'backgroundSelectedDark' : 'backgroundTableHeader'}
        padding={[4]}
        borderRadius={[2]}
        justifyContent="between"
      >
        <RowFlexContainer justifyContent="between" alignItems="start" position="relative">
          <ColumnFlexContainer gap={[1]}>
            <StyledUppercaseText type={isMobile ? 'body4' : 'button'}>
              {t(
                bopisLeadTimeTranslations.pickPlusPackMinutes.key,
                bopisLeadTimeTranslations.pickPlusPackMinutes.defaultValue,
              )}
            </StyledUppercaseText>

            <SecondaryTextTypography type="legal">
              {t(
                bopisLeadTimeTranslations.totalLeadTime.key,
                bopisLeadTimeTranslations.totalLeadTime.defaultValue,
              )}
            </SecondaryTextTypography>
          </ColumnFlexContainer>
          {isAboveThreshold && (
            <Tooltip
              text={
                <Typography type="caption" color="white">
                  {t(
                    bopisLeadTimeTranslations.aboveThresholdTooltipText.key,
                    bopisLeadTimeTranslations.aboveThresholdTooltipText.defaultValue,
                  )}
                </Typography>
              }
              position="bottom"
              arrowedToolTip
            >
              <div>
                <RowFlexContainer
                  style={{
                    backgroundColor: percentToHex(
                      muiTheme.color[getTresholdColor(isAboveThreshold, isDarkMode)],
                      8,
                    ),
                    transform: 'scale(0.8)',
                  }}
                  padding={[2]}
                  gap={[1]}
                  borderRadius={[1]}
                  alignItems="center"
                  justifyContent="center"
                >
                  <Icon icon={Icons.WARNING} size="small" color={getTresholdColor(isAboveThreshold, isDarkMode)} />
                  <StyledThresholdText type="caption" shadeColor={getTresholdColor(isAboveThreshold, isDarkMode)}>
                    {t(
                      bopisLeadTimeTranslations.aboveThreshold.key,
                      bopisLeadTimeTranslations.aboveThreshold.defaultValue,
                    )}
                  </StyledThresholdText>
                </RowFlexContainer>
              </div>
            </Tooltip>
          )}

          <RowFlexContainer position="absolute" top="-4px" right="-4px">
            <Tooltip
              text={
                <Typography type="caption" color="white">
                  {t(
                    bopisLeadTimeTranslations.pickMinutesPackMinutesTooltipText.key,
                    bopisLeadTimeTranslations.pickMinutesPackMinutesTooltipText.defaultValue,
                  )}
                </Typography>
              }
              position="bottom"
              arrowedToolTip
            >
              <Icon icon={Icons.INFO} size="small" />
            </Tooltip>
          </RowFlexContainer>
        </RowFlexContainer>

        <RowFlexContainer gap={[4]} alignItems="center">
          <MinutesItem
            label={t(bopisLeadTimeTranslations.pickText.key, bopisLeadTimeTranslations.pickText.defaultValue)}
            value={getMinutesTextAndSymbol(pickMinutes).text}
            symbol={getMinutesTextAndSymbol(pickMinutes).symbol}
          />

          <IconSeparator icon={Icons.PLUS} />

          <MinutesItem
            label={t(bopisLeadTimeTranslations.packText.key, bopisLeadTimeTranslations.packText.defaultValue)}
            value={getMinutesTextAndSymbol(packMinutes).text}
            symbol={getMinutesTextAndSymbol(packMinutes).symbol}
          />

          <IconSeparator icon={Icons.ARROW} />

          <MinutesItem
            label={t(bopisLeadTimeTranslations.totalText.key, bopisLeadTimeTranslations.totalText.defaultValue)}
            value={getMinutesTextAndSymbol(pickMinutes + packMinutes).text}
            symbol={getMinutesTextAndSymbol(pickMinutes + packMinutes).symbol}
            color={getTresholdColor(isAboveThreshold, isDarkMode)}
            valueTextType="h2"
          />
        </RowFlexContainer>
      </ColumnFlexContainer>
    </ColumnFlexContainer>
  );
};
